import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAppScale } from "@/contexts/AppScaleContext";
import magicWheelIcon from "@assets/web-v3-images//icons/magic-wheel-icon.png";
import facebookIcon from "@assets/web-v3-images//icons/facebook-icon.png";
import tiktokIcon from "@assets/web-v3-images//icons/tiktok-icon.png";
import teleIcon from "@assets/web-v3-images//icons/tele-icon.png";
import { getDataApi } from "@/config/service";
import API_PATH from "@/config/API_PATH";
import { mediaQueryPoint, useMediaQuery } from "@/utils/hooks";
// GA4 — chien dich chung Thadingyut 2026 (sheet 12_KB_Event ba dich vu)
import {
  isThadingyutIcon,
  withSrcParam,
  trackPromoExit,
} from "@/analytics/thadingyut";
import useThadingyutImpression from "@/analytics/useThadingyutImpression";

// Chiều cao thanh handle (h-11) — phần luôn lộ ra khi khay thu gọn
const HANDLE_HEIGHT = 44;

// Thời gian không tương tác trước khi khay tự thu gọn (ms).
const AUTO_COLLAPSE_MS = 4000;

/**
 * Khay dưới cùng: Magic Wheel + các kênh mạng xã hội.
 * Luôn nổi cố định trên mọi phần tử (fixed, z-50). Hoạt động như drawer:
 * chạm/nhấn giữ rồi kéo lên-xuống để mở/đóng (thả quá nửa hành trình thì
 * snap sang trạng thái kia), hoặc bấm handle để toggle. Khi thu gọn chỉ còn
 * thanh handle với mũi tên hướng lên, thỉnh thoảng nhún nhẹ để gợi ý mở.
 */
const SocialLinks = ({ onSelect }) => {
  const { t } = useTranslation();
  // Đã portal ra document.body (xem return bên dưới) nên KHÔNG còn tự động ăn
  // theo transform:scale của LayoutApp -> phải tự đọc tỉ lệ này từ context rồi
  // áp lại bằng tay để vẫn thu nhỏ đồng bộ với phần còn lại của trang.
  const { scale } = useAppScale();
  const [expanded, setExpanded] = useState(true);
  // Vị trí kéo hiện tại (px, 0 = mở hết) — null khi không kéo
  const [dragY, setDragY] = useState(null);
  const sheetRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const suppressClickTimerRef = useRef(null);
  // Đồng hồ đếm để tự thu gọn khi không tương tác.
  const collapseTimerRef = useRef(null);

  // const items = [
  //   {
  //     key: "magicWheel",
  //     icon: magicWheelIcon,
  //     // Deep link mở app MyTel (scheme mytel://) -> KHÔNG mở tab mới.
  //     href: "mytel://webgamemyid?ref=http%3A%2F%2Fmagicwheel.com.mm%2F/",
  //   },
  //   {
  //     key: "facebook",
  //     icon: facebookIcon,
  //     href: "https://www.facebook.com/profile.php?id=100095614059920",
  //   },
  //   { key: "tiktok", icon: tiktokIcon, href: "https://www.tiktok.com/@DigitalFunMM" },
  //   { key: "teleGroup", icon: teleIcon, href: "https://t.me/+vNMCGiZcFFYzNTNl" },
  // ];

  const [items, setItems] = useState([]);
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoint.md}px)`);

  // KHÔNG cache danh sách icon (trước đây lưu cookie "crossIcons" 15 phút): CMS
  // đổi icon/link thì client vẫn hiển thị bản cũ tới khi cookie hết hạn. Header
  // cũng gọi thẳng API mỗi lần mount nên giữ nguyên cách này cho đồng bộ.
  const handleGetDataSocial = async () => {
    try {
      const iconsData = await getDataApi(API_PATH.crossIcons);
      const { data } = await getDataApi(API_PATH.getSocialIcons);
      let formatData = [];
      if (iconsData && iconsData.data) {
        formatData = [
          ...formatData,
          // Giữ CẢ hai link, chọn theo thiết bị lúc render. Không chốt ở đây vì
          // useMediaQuery trả false ở lần render đầu (chỉ set trong effect) nên
          // isMobile luôn sai tại thời điểm fetch.
          ...iconsData.data.map((item) => ({
            key: item.title,
            icon: item.image_path,
            href: item.href,
            hrefMobile: item.href_mobile,
            isGetApi: true,
          })),
        ];
      }
      if (data) {
        formatData = [
          ...formatData,
          ...data.map((item) => ({
            key: item.title,
            icon: item.image_path,
            href: item.href,
            isGetApi: true,
          })),
        ];
      }
      setItems(formatData);
    } catch (error) {
      setItems([]);
    }
  };

  useEffect(() => {
    handleGetDataSocial();
  }, []);

  // Hành trình kéo tối đa = phần khay bị giấu khi thu gọn
  const maxShift = () =>
    sheetRef.current ? sheetRef.current.offsetHeight - HANDLE_HEIGHT : 0;

  const clampShift = (y) => Math.min(Math.max(y, 0), maxShift());

  const clearCollapseTimer = () => {
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
  };

  // Đặt lại đồng hồ: sau AUTO_COLLAPSE_MS không tương tác thì thu gọn khay.
  const scheduleCollapse = () => {
    clearCollapseTimer();
    collapseTimerRef.current = setTimeout(
      () => setExpanded(false),
      AUTO_COLLAPSE_MS,
    );
  };

  // Chỉ hẹn giờ khi đang MỞ và KHÔNG kéo; còn lại thì huỷ. Mở khay hay đổi trạng
  // thái kéo đều chạy lại effect này -> coi như "reset" đồng hồ mỗi lần tương tác.
  useEffect(() => {
    if (expanded && dragY === null) {
      scheduleCollapse();
    } else {
      clearCollapseTimer();
    }
    return clearCollapseTimer;
  }, [expanded, dragY]);

  const handlePointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // Chạm vào khay = tương tác -> đẩy lùi thời điểm tự thu gọn.
    if (expanded) scheduleCollapse();
    dragRef.current = {
      pointerId: e.pointerId,
      startY: e.clientY,
      base: expanded ? 0 : maxShift(),
      moved: false,
    };
    // KHÔNG bắt con trỏ ở đây: khi pointer capture còn hiệu lực lúc pointerup,
    // spec bắt trình duyệt bắn `click` vào phần tử giữ capture chứ không phải
    // phần tử được hit-test. Chỉ capture khi đã thực sự kéo (handlePointerMove).
  };

  const handlePointerMove = (e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const delta = e.clientY - d.startY;
    // Dưới ngưỡng này coi là tap, để onClick của nút hoạt động bình thường
    if (!d.moved && Math.abs(delta) < 6) return;
    if (!d.moved) {
      d.moved = true;
      // Đã vượt ngưỡng -> chắc chắn là kéo, giữ con trỏ để không tuột giữa chừng.
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    setDragY(clampShift(d.base + delta));
  };

  const handlePointerEnd = (e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (d.moved) {
      // Thả quá nửa hành trình → thu gọn, ngược lại → mở
      const shift = clampShift(d.base + (e.clientY - d.startY));
      setExpanded(shift < maxShift() / 2);
      // Cờ chỉ có hiệu lực cho cú click sinh ra ngay sau cú kéo này. Phải tự hết
      // hạn: kéo mà kết thúc ngoài khay thì KHÔNG có click nào theo sau, cờ sẽ
      // treo lại và nuốt mất cú chạm hợp lệ kế tiếp vào icon.
      clearTimeout(suppressClickTimerRef.current);
      suppressClickRef.current = true;
      suppressClickTimerRef.current = setTimeout(() => {
        suppressClickRef.current = false;
      }, 300);
    }
    setDragY(null);
  };

  // Chặn click phát sinh sau khi kéo (kẻo toggle ngược hoặc mở nhầm link)
  const handleClickCapture = (e) => {
    if (suppressClickRef.current) {
      clearTimeout(suppressClickTimerRef.current);
      suppressClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const dragging = dragY !== null;

  /* Portal thẳng ra document.body (giống Radix modal) để KHÔNG nằm trong cây bị
     LayoutApp transform:scale (nếu không, position:fixed sẽ phân giải theo tổ
     tiên có transform thay vì viewport -> bị cuộn theo trang và bị thu nhỏ theo
     app). Portal ra body thì containing block của `fixed` luôn là viewport thật
     -> không bị ảnh hưởng bởi scroll lẫn scale toàn app. */
  return createPortal(
    <section
      onClick={() => { // chuyển click để mở thay vì phải kéo
        if (suppressClickRef.current) return;
        setExpanded((prev) => !prev);
      }}
      ref={sheetRef}
      // Chạm vào bất cứ đâu trong khay = còn đang dùng -> đẩy lùi tự thu gọn.
      // Việc mở/đóng thì chỉ thanh handle bên dưới mới điều khiển.
      onPointerDown={() => expanded && scheduleCollapse()}
      /* Gộp scale + thu gọn/kéo vào MỘT transform, thứ tự scale() TRƯỚC
         translateY() để phần translate được nhân theo scale -> lúc thu gọn vẫn
         chừa đúng tay cầm (44px × scale) ở đáy, không bị trượt khỏi màn hình.
         (Nếu để `transform:scale` và property `translate` riêng thì translate KHÔNG
         bị scale nên đẩy khay tụt gần trọn chiều cao gốc -> mất khỏi màn hình.)
         origin bottom-center để khay luôn dính đáy. */
      style={{
        transform: `scale(${scale}) translateY(${dragging ? `${dragY}px` : expanded ? "0px" : "calc(100% - 2.75rem)"
          })`,
        transformOrigin: "bottom center",
      }}
      className={`@container fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-3xl rounded-t-3xl bg-linear-to-b from-[#FCF3DE] to-[#FBEACF] shadow-[0_-8px_28px_rgba(200,110,40,0.4)] ${dragging ? "" : "transition-transform duration-300 ease-out"
        }`}
    >
      {/* Chỉ thanh handle này nhận thao tác mở/đóng: bấm để toggle, hoặc kéo
          lên-xuống rồi thả (quá nửa hành trình thì snap sang trạng thái kia).
          Vùng icon bên dưới không dính drag nữa nên bấm icon là điều hướng ngay.
          touch-none đặt tại đây để chặn cuộn trang khi kéo handle, còn phần icon
          vẫn giữ hành vi chạm mặc định. */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation(); // chặn bubble lên section, tránh double-toggle
          setExpanded((v) => !v);
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onClickCapture={handleClickCapture}
        aria-expanded={expanded}
        aria-label={t(expanded ? "home.social.collapse" : "home.social.expand")}
        className={`flex h-11 w-full cursor-grab touch-none items-center justify-center active:cursor-grabbing ${!expanded && !dragging
            ? "animate-nudge motion-reduce:animate-none"
            : ""
          }`}
      >
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
        >
          <path
            d="M6 14.5l6-6 6 6"
            stroke="#8a3213"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <ul className="grid grid-cols-4 gap-y-[clamp(12px,3cqw,24px)] px-[5cqw] pb-[clamp(16px,3.5cqw,32px)]">
        {items.map((item) => (
          <SocialItem
            key={item.key}
            item={item}
            isMobile={isMobile}
            onSelect={onSelect}
            t={t}
          />
        ))}
      </ul>
    </section>,
    document.body,
  );
};

/**
 * Mot icon trong khay: cross-service (CMS, bang CROSS_ICON) hoac mang xa hoi.
 *
 * GA4 Thadingyut 2026 (sheet 12_KB_Event ba dich vu) — CHI ap dung cho icon
 * dan ve trang chien dich, cac icon khac giu nguyen hanh vi cu:
 *   - view_promotion   : 50% dien tich, lien tuc 1 giay (xem useThadingyutImpression)
 *   - select_promotion : khi bam
 *   - exit_to_promo_hub: ngay truoc khi roi ShweDeal sang trang chien dich
 * Link duoc gan tham so thuong ?src=shwedeal, TUYET DOI khong gan utm_*.
 */
const SocialItem = ({ item, isMobile, onSelect, t }) => {
  const ref = useRef(null);

  // Trên mobile ưu tiên href_mobile, thiếu thì lùi về href thường.
  const rawHref = (isMobile ? item.hrefMobile || item.href : item.href) || "#";
  const isPromo = isThadingyutIcon(rawHref);

  // Gan ?src= ngay tren href de nguoi dung copy link cung dung nguon.
  // Voi deep link mytel://...?ref=<url>, ?src= duoc gan vao URL ben trong.
  const href = isPromo ? withSrcParam(rawHref) : rawHref;

  // http(s) mo tab moi -> trang hien tai khong unload, hit GA4 gui binh thuong.
  const opensNewTab = href.startsWith("http");

  useThadingyutImpression(ref, isPromo);

  const handleClick = (e) => {
    onSelect?.(item.key);
    if (!isPromo) return;
    // Tra ve true nghia la ham tu lo viec chuyen huong (deep link, cung tab)
    if (trackPromoExit(rawHref, opensNewTab)) {
      e.preventDefault();
    }
  };

  return (
    <li className="flex justify-center">
      {/* http(s) mở tab mới; deep link mytel:// mở ngay context hiện tại để
        app bắt được scheme. */}
      <a
        ref={ref}
        href={href}
        target={opensNewTab ? "_blank" : undefined}
        rel="noopener noreferrer"
        onClick={handleClick}
        className="btn-click-active flex flex-col items-center gap-[clamp(6px,2cqw,18px)]"
      >
        <img
          src={item.icon}
          alt=""
          width={72}
          height={72}
          className="size-[clamp(48px,11cqw,96px)] object-contain"
        />
        <span className="whitespace-nowrap text-[clamp(12px,3.2cqw,26px)] font-bold text-[#8a3213]">
          {item.isGetApi ? item.key : t(`home.social.${item.key}`)}
        </span>
      </a>
    </li>
  );
};

export default SocialLinks;
